// Release C-1 · Shared "Open in Microsoft 365 Mail" action.
// Owner + Administrator only. Opens https://outlook.office.com/mail/ in a new
// tab with rel="noopener noreferrer". If a Graph message webLink is known for
// the record, the action becomes "Open in Outlook" and opens that message.
// Click is audited via POST /api/admin/integrations/microsoft365/open-in-outlook.
import React from "react";
import { Mail, ExternalLink } from "lucide-react";
import axios from "axios";
import { useAuth } from "../../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OUTLOOK_MAILBOX = "https://outlook.office.com/mail/";

export default function OpenInMicrosoftMailButton({
  surface,              // required: "owner_command_center" | "lead_detail" | ...
  recordType = null,
  recordId = null,
  messageId = null,     // if the record has a stored Graph message id
  webLink = null,       // if the record has a stored Graph webLink
  size = "sm",
  variant = "outline",
  className = "",
  label = null,
  iconOnly = false,     // FINAL-POLISH P2 — compact affordance for row cells
}) {
  const { user } = useAuth();
  const role = user?.role_key;
  const allowed = role === "OWNER_SUPER_ADMIN" || role === "ADMINISTRATOR";
  if (!allowed) return null;

  const hasDeepLink = Boolean(webLink && messageId);
  const href = hasDeepLink ? webLink : OUTLOOK_MAILBOX;
  const finalLabel = label || (hasDeepLink
    ? "Open in Outlook"
    : "Open in Microsoft 365 Mail");

  const onClick = () => {
    // Audit fire-and-forget; UI never blocks on it.
    axios.post(`${API}/admin/integrations/microsoft365/open-in-outlook`, {
      surface,
      record_type: recordType,
      record_id: recordId,
      message_id: messageId,
    }).catch(() => {});
  };

  const isPill = size === "pill";
  let style;
  if (iconOnly) {
    style = {
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 44, height: 44, padding: 0, borderRadius: 4,  // B2a: 44x44 touch target
      background: "white", color: "#12233F", border: "1px solid #cbd5e1",
      cursor: "pointer", textDecoration: "none",
    };
  } else if (isPill) {
    style = {
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "6px 12px", borderRadius: 9999, minHeight: 44,
      background: "transparent", color: "#12233F",
      border: "1px solid #C6A15B",
      fontSize: 11, letterSpacing: 1, textTransform: "uppercase",
      fontFamily: "'Space Mono', monospace",
      textDecoration: "none", cursor: "pointer",
    };
  } else {
    style = {
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "6px 12px", borderRadius: 4, minHeight: 44,
      background: "white", color: "#12233F", border: "1px solid #cbd5e1",
      fontSize: 12, cursor: "pointer", textDecoration: "none",
    };
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      data-testid={`open-in-outlook-${surface}${recordId ? `-${recordId}` : ""}`}
      className={className}
      style={style}
      title={hasDeepLink
        ? "Opens the exact Outlook message linked to this record in a new tab"
        : "Opens Microsoft 365 Mail (outlook.office.com) in a new tab"}
      aria-label={finalLabel}
    >
      <Mail size={iconOnly ? 14 : 14} aria-hidden="true" />
      {!iconOnly && (
        <>
          {finalLabel}
          <ExternalLink size={12} aria-hidden="true" />
        </>
      )}
    </a>
  );
}
